import DeliveryLifecycleStatus from "../../components/DeliveryLifecycleStatus";
import ManagementEmailButton from '../DonorManagement/ManagementEmailButton';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { updateAccountName, updateAccountPicture } from '../../api/accountApi';
import {
  createHouseholdListener,
  fetchHouseholds,
  Household,
  HouseholdListener,
  removeHouseholdMember,
  updateHouseholdPreferences,
} from '../../api/householdsApi';
import { updateListener } from '../../api/listenersApi';
import { ApiError, toApiError } from '../../api/http';
import {
  cancelVoiceInvitation,
  createManualVoiceInvitationLink,
  createVoiceInvitation,
  fetchVoiceInvitations,
  isVoiceInvitationCaptureUrl,
  replaceLegacyVoiceInvitation,
  resendVoiceInvitation,
  VoiceInvitationStatus,
} from '../../api/voiceInvitationsApi';
import { BookIcon, CheckIcon, CloseIcon, MicIcon, MoonIcon, PencilIcon, PlusIcon } from '../../components/icons';
import { EmptyState, FormField, PillButton, SettingsRow, StateMessage } from '../../components/ui';
import { HomeIcon } from '../../components/ui/uiIcons';
import AnalyticsPreference from '../../components/AnalyticsPreference/AnalyticsPreference';
import './AccountFamily.css';

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'UTC',
];

const AGE_BANDS = [
  { value: '', label: 'Prefer not to say' },
  { value: '0-2', label: 'Baby or toddler (0-2)' },
  { value: '3-5', label: 'Preschool (3-5)' },
  { value: '6-8', label: 'Early reader (6-8)' },
  { value: '9-12', label: 'Independent reader (9-12)' },
  { value: '13+', label: 'Teen (13+)' },
];

function initials(name: string) {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

function timeInput(value: string | null) {
  return value ? value.slice(0, 5) : '';
}

function roleLabel(role: string) {
  return role === 'owner' ? 'Household owner' : role === 'admin' ? 'Admin' : 'Adult';
}

function invitationStateLabel(state: string) {
  const labels: Record<string, string> = {
    draft: 'Link not shared',
    pending: 'Waiting for a recording',
    accepted: 'Accepted',
    consented: 'Consent complete',
    capturing: 'Recording in progress',
    completed: 'Recording complete',
    expired: 'Expired',
    revoked: 'Revoked',
  };
  return labels[state] || 'Status needs attention';
}

function invitationStatusTone(invitation: VoiceInvitationStatus) {
  if (invitation.state === 'revoked') return 'revoked';
  if (invitation.requires_updated_consent_form) return 'required';
  return invitation.consent_status;
}

function invitationStatusLabel(invitation: VoiceInvitationStatus) {
  if (invitation.state === 'revoked') return 'Invitation revoked';
  if (invitation.requires_updated_consent_form) return 'Updated consent form needed';
  if (invitation.consent_status === 'active') return 'Consent active';
  if (invitation.consent_status === 'revoked') return 'Consent revoked';
  return 'Consent required';
}

function invitationDeliveryLabel(status: string) {
  const labels: Record<string, string> = {
    disabled: 'Share link only',
    queued: 'Email queued',
    submitted: 'Email submitted',
    delivered: 'Email delivered',
    failed: 'Email needs attention',
  };
  return labels[status] || 'Delivery pending';
}

function hasEmailDelivery(status: string) {
  return ['queued', 'submitted', 'delivered', 'failed'].includes(status);
}

function formatExpiry(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Expiry unavailable';
  return `Expires ${new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)}`;
}

const AccountFamily: React.FC = () => {
  const { state, dispatch, fetchVoices } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const expandedSection = ['account-section', 'family-section', 'bedtime-section'].includes(location.hash.slice(1)) ? location.hash.slice(1) : null;
  const pendingFocus = useRef<string | null>(null);
  const openSection = (id: string) => navigate({ pathname: location.pathname, search: location.search, hash: expandedSection === id ? '' : `#${id}` });
  const restoreFocus = (id: string) => { pendingFocus.current = id; };
  useEffect(() => {
    const target = pendingFocus.current && document.getElementById(pendingFocus.current);
    if (target && !target.hasAttribute('disabled')) { target.focus(); pendingFocus.current = null; }
  });
  const [households, setHouseholds] = useState<Household[]>([]);
  const [selectedHouseholdId, setSelectedHouseholdId] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<ReturnType<typeof toApiError> | null>(null);
  const [loadRetryNonce, setLoadRetryNonce] = useState(0);
  const [notice, setNotice] = useState('');
  const [actionError, setActionError] = useState<ReturnType<typeof toApiError> | null>(null);

  const [accountName, setAccountName] = useState(state.user?.full_name || state.user?.name || '');
  const [accountBusy, setAccountBusy] = useState(false);
  const [pictureBusy, setPictureBusy] = useState(false);
  const [invitations, setInvitations] = useState<VoiceInvitationStatus[]>([]);
  const [invitationError, setInvitationError] = useState<ReturnType<typeof toApiError> | null>(null);
  const [invitationRetryNonce, setInvitationRetryNonce] = useState(0);
  const [invitationBusyId, setInvitationBusyId] = useState<string | null>(null);
  const [freshInvitationLink, setFreshInvitationLink] = useState<{
    invitation_id: string;
    capture_url: string;
  } | null>(null);
  const [copyFeedback, setCopyFeedback] = useState('');

  const [timezone, setTimezone] = useState('UTC');
  const [quietStart, setQuietStart] = useState('');
  const [quietEnd, setQuietEnd] = useState('');
  const [renderReadyEmail, setRenderReadyEmail] = useState(false);
  const [preferencesBusy, setPreferencesBusy] = useState(false);

  const [addingListener, setAddingListener] = useState(false);
  const [newListenerName, setNewListenerName] = useState('');
  const [newListenerAge, setNewListenerAge] = useState('');
  const [listenerBusyId, setListenerBusyId] = useState<string | null>(null);
  const [memberBusyId, setMemberBusyId] = useState<string | null>(null);
  const [editingListenerId, setEditingListenerId] = useState<string | null>(null);
  const [listenerName, setListenerName] = useState('');
  const [listenerAge, setListenerAge] = useState('');

  useEffect(() => {
    let active = true;
    setLoadError(null);
    Promise.all([fetchHouseholds(), fetchVoices(true)])
      .then(([rows]) => {
        if (!active) return;
        setHouseholds(rows);
        setSelectedHouseholdId((current) => current || rows[0]?.household_id || '');
      })
      .catch((error) => {
        if (active) setLoadError(toApiError(error));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [fetchVoices, loadRetryNonce]);

  const household = useMemo(
    () => households.find((row) => row.household_id === selectedHouseholdId) || households[0],
    [households, selectedHouseholdId]
  );
  const activeHouseholdId = household?.household_id;

  useEffect(() => {
    if (!household) return;
    setTimezone(household.preferences.timezone);
    setQuietStart(timeInput(household.preferences.quiet_hours_start));
    setQuietEnd(timeInput(household.preferences.quiet_hours_end));
    setRenderReadyEmail(household.preferences.render_ready_email);
  }, [household]);

  useEffect(() => {
    if (!activeHouseholdId) return;
    let active = true;
    setInvitationError(null);
    fetchVoiceInvitations(activeHouseholdId)
      .then((rows) => {
        if (active) setInvitations(rows);
      })
      .catch((error) => {
        if (active) setInvitationError(toApiError(error));
      });
    return () => { active = false; };
  }, [activeHouseholdId, invitationRetryNonce]);

  useEffect(() => {
    if (loading || !expandedSection) return;
    const headingId = expandedSection === 'family-section' ? 'adults-heading' : expandedSection.replace('-section', '-heading');
    const focusHeading = () => {
      const heading = document.getElementById(headingId);
      heading?.focus({ preventScroll: true });
      heading?.scrollIntoView?.({ block: 'start' });
    };
    // Initial fragment navigation and history traversal can apply native focus
    // after the section mounts. Let that paint finish before restoring the
    // section heading, including when household bootstrap resolves slowly.
    if (navigationType === 'POP') {
      let frame = requestAnimationFrame(() => { frame = requestAnimationFrame(focusHeading); });
      return () => cancelAnimationFrame(frame);
    }
    focusHeading();
  }, [expandedSection, loading, location.key, navigationType]);

  const replaceHousehold = (next: Household) => {
    setHouseholds((rows) => rows.map((row) => (
      row.household_id === next.household_id ? next : row
    )));
  };

  const showResult = (message: string) => {
    setActionError(null);
    setNotice(message);
  };

  const showError = (error: unknown) => {
    setNotice('');
    setActionError(toApiError(error));
  };

  const saveAccount = async () => {
    if (!state.user || !accountName.trim()) return;
    setAccountBusy(true);
    try {
      const profile = await updateAccountName(accountName.trim());
      dispatch({
        type: 'SET_USER',
        payload: {
          ...state.user,
          full_name: profile.full_name || undefined,
          name: profile.full_name || profile.email,
        },
      });
      setAccountName(profile.full_name || '');
      showResult('Account name saved.');
      const rows = await fetchHouseholds();
      setHouseholds(rows);
    } catch (error) {
      showError(error);
    } finally {
      setAccountBusy(false);
    }
  };

  const savePicture = async (file?: File) => {
    if (!file || !state.user) return;
    setPictureBusy(true);
    try {
      const profile = await updateAccountPicture(file);
      dispatch({
        type: 'SET_USER',
        payload: { ...state.user, picture: profile.profile_picture_url || undefined },
      });
      showResult('Profile picture saved.');
      const rows = await fetchHouseholds();
      setHouseholds(rows);
    } catch (error) {
      showError(error);
    } finally {
      setPictureBusy(false);
    }
  };

  const savePreferences = async () => {
    if (!household) return;
    setPreferencesBusy(true);
    try {
      const preferences = await updateHouseholdPreferences(household.household_id, {
        timezone,
        quiet_hours_start: quietStart || null,
        quiet_hours_end: quietEnd || null,
        render_ready_email: renderReadyEmail,
      });
      replaceHousehold({ ...household, preferences });
      showResult('Family preferences saved.');
    } catch (error) {
      showError(error);
    } finally {
      setPreferencesBusy(false);
    }
  };

  const addListener = async () => {
    if (!household || !newListenerName.trim()) return;
    setListenerBusyId('new');
    try {
      const listener = await createHouseholdListener(
        household.household_id,
        newListenerName.trim(),
        newListenerAge || null
      );
      replaceHousehold({
        ...household,
        listeners: [...household.listeners, listener],
      });
      setNewListenerName('');
      setNewListenerAge('');
      restoreFocus(`listener-edit-${listener.listener_id}`);
      setAddingListener(false);
      showResult(`${listener.display_name} was added to your family.`);
    } catch (error) {
      showError(error);
    } finally {
      setListenerBusyId(null);
    }
  };

  const beginListenerEdit = (listener: HouseholdListener) => {
    setEditingListenerId(listener.listener_id);
    setListenerName(listener.display_name);
    setListenerAge(listener.age_band || '');
  };

  const saveListener = async (listenerId: string) => {
    if (!household || !listenerName.trim()) return;
    setListenerBusyId(listenerId);
    try {
      const updated = await updateListener(listenerId, {
        display_name: listenerName.trim(),
        age_band: listenerAge || null,
      });
      replaceHousehold({
        ...household,
        listeners: household.listeners.map((listener) => (
          listener.listener_id === listenerId
            ? {
                ...listener,
                display_name: updated.display_name,
                age_band: updated.age_band,
                state: updated.state as 'active' | 'archived',
              }
            : listener
        )),
      });
      restoreFocus(`listener-edit-${listenerId}`);
      setEditingListenerId(null);
      showResult(`${updated.display_name}'s profile was saved.`);
    } catch (error) {
      showError(error);
    } finally {
      setListenerBusyId(null);
    }
  };

  const setListenerState = async (listener: HouseholdListener) => {
    if (!household) return;
    const nextState = listener.state === 'active' ? 'archived' : 'active';
    if (nextState === 'archived' && !window.confirm(
      `Archive ${listener.display_name}? Existing stories stay in your history, but this listener will be hidden from new bedtime flows.`
    )) return;
    setListenerBusyId(listener.listener_id);
    try {
      const updated = await updateListener(listener.listener_id, { state: nextState });
      replaceHousehold({
        ...household,
        listeners: household.listeners.map((row) => (
          row.listener_id === listener.listener_id ? { ...row, state: updated.state as 'active' | 'archived' } : row
        )),
      });
      restoreFocus(`listener-state-${listener.listener_id}`);
      showResult(`${listener.display_name} was ${nextState === 'active' ? 'restored' : 'archived'}.`);
    } catch (error) {
      showError(error);
    } finally {
      setListenerBusyId(null);
    }
  };

  const removeAdult = async (membershipId: string, displayName: string) => {
    if (!household || !window.confirm(
      `Remove ${displayName} from this family? They will immediately lose access to its voices, listeners, and stories. Their Luma account will not be deleted.`
    )) return;
    setMemberBusyId(membershipId);
    try {
      await removeHouseholdMember(household.household_id, membershipId);
      replaceHousehold({
        ...household,
        members: household.members.filter((member) => member.membership_id !== membershipId),
      });
      restoreFocus('adults-heading');
      showResult(`${displayName} was removed from this family.`);
    } catch (error) {
      showError(error);
    } finally {
      setMemberBusyId(null);
    }
  };

  const resendInvitation = async (invitation: VoiceInvitationStatus) => {
    setInvitationBusyId(invitation.invitation_id);
    setFreshInvitationLink(null);
    setCopyFeedback('');
    try {
      const result = await resendVoiceInvitation(invitation.invitation_id);
      if (result.capture_url !== undefined && !isVoiceInvitationCaptureUrl(result.capture_url)) {
        throw new ApiError('service_unavailable');
      }
      if (result.capture_url === undefined && !hasEmailDelivery(result.delivery_status)) {
        throw new ApiError('service_unavailable');
      }
      setInvitations((rows) => rows.map((row) => (
        row.invitation_id === invitation.invitation_id
          ? {
              ...row,
              state: result.state,
              delivery_status: result.delivery_status,
              expires_at: result.expires_at,
              is_expired: false,
              can_resend: true,
              can_revoke: row.can_revoke,
            }
          : row
      )));
      if (result.capture_url) {
        setFreshInvitationLink({
          invitation_id: invitation.invitation_id,
          capture_url: result.capture_url,
        });
      }
      setInvitations((rows) => rows.map((row) => (
        row.invitation_id === invitation.invitation_id
          ? { ...row, can_invite_again: false }
          : row
      )));
      showResult(result.capture_url
        ? 'A fresh invitation link is ready. The older link no longer works.'
        : 'A fresh invitation email is queued. The older link no longer works.');
    } catch (error) {
      showError(error);
    } finally {
      setInvitationBusyId(null);
    }
  };

  const createManualInvitationLink = async (invitation: VoiceInvitationStatus) => {
    setInvitationBusyId(invitation.invitation_id);
    setFreshInvitationLink(null);
    setCopyFeedback('');
    try {
      const result = await createManualVoiceInvitationLink(invitation.invitation_id);
      if (!isVoiceInvitationCaptureUrl(result.capture_url)) {
        throw new ApiError('service_unavailable');
      }
      setInvitations((rows) => rows.map((row) => (
        row.invitation_id === invitation.invitation_id
          ? {
              ...row,
              state: result.state,
              delivery_status: result.delivery_status,
              expires_at: result.expires_at,
              is_expired: false,
            }
          : row
      )));
      setFreshInvitationLink({
        invitation_id: invitation.invitation_id,
        capture_url: result.capture_url,
      });
      showResult('A manual recovery link is ready. Any older link no longer works.');
    } catch (error) {
      showError(error);
    } finally {
      setInvitationBusyId(null);
    }
  };

  const createUpdatedInvitation = async (invitation: VoiceInvitationStatus) => {
    if (!activeHouseholdId) return;
    setInvitationBusyId(invitation.invitation_id);
    setFreshInvitationLink(null);
    setCopyFeedback('');
    try {
      const result = await replaceLegacyVoiceInvitation(invitation.invitation_id);
      if (result.capture_url !== undefined && !isVoiceInvitationCaptureUrl(result.capture_url)) {
        throw new ApiError('service_unavailable');
      }
      if (result.capture_url === undefined && !hasEmailDelivery(result.delivery_status)) {
        throw new ApiError('service_unavailable');
      }
      if (result.capture_url) {
        setFreshInvitationLink({
          invitation_id: invitation.invitation_id,
          capture_url: result.capture_url,
        });
      }
      try {
        const refreshed = await fetchVoiceInvitations(activeHouseholdId);
        setInvitations(refreshed);
        if (result.capture_url) {
          setFreshInvitationLink({
            invitation_id: result.invitation_id,
            capture_url: result.capture_url,
          });
        }
      } catch (refreshError) {
        setInvitationError(toApiError(refreshError));
      }
      showResult(result.capture_url
        ? 'An updated invitation is ready. The previous link no longer works.'
        : 'An updated invitation email is queued. The previous link no longer works.');
    } catch (error) {
      showError(error);
    } finally {
      setInvitationBusyId(null);
    }
  };

  const inviteAgain = async (invitation: VoiceInvitationStatus) => {
    if (
      !activeHouseholdId
      || !invitation.recipient_email
      || invitation.listeners.length === 0
    ) {
      showError(new ApiError('service_unavailable'));
      return;
    }
    setInvitationBusyId(invitation.invitation_id);
    setFreshInvitationLink(null);
    setCopyFeedback('');
    try {
      const result = await createVoiceInvitation(
        invitation.recipient_email,
        activeHouseholdId,
        invitation.listeners.map((listener) => listener.listener_id),
      );
      if (result.capture_url !== undefined && !isVoiceInvitationCaptureUrl(result.capture_url)) {
        throw new ApiError('service_unavailable');
      }
      if (result.capture_url === undefined && !hasEmailDelivery(result.delivery_status)) {
        throw new ApiError('service_unavailable');
      }
      if (result.capture_url) {
        setFreshInvitationLink({
          invitation_id: invitation.invitation_id,
          capture_url: result.capture_url,
        });
      }
      try {
        const refreshed = await fetchVoiceInvitations(activeHouseholdId);
        setInvitations(refreshed);
        if (result.capture_url) {
          setFreshInvitationLink({
            invitation_id: result.invitation_id,
            capture_url: result.capture_url,
          });
        }
      } catch (refreshError) {
        setInvitationError(toApiError(refreshError));
      }
      showResult(result.capture_url
        ? 'A new invitation is ready. The revoked link still cannot be used.'
        : 'A new invitation email is queued. The revoked link still cannot be used.');
    } catch (error) {
      showError(error);
    } finally {
      setInvitationBusyId(null);
    }
  };

  const revokeInvitation = async (invitation: VoiceInvitationStatus) => {
    if (!window.confirm(
      `Revoke the invitation for ${invitation.recipient_email || 'this voice donor'}? The link will stop working.`
    )) return;
    setInvitationBusyId(invitation.invitation_id);
    setFreshInvitationLink(null);
    setCopyFeedback('');
    try {
      await cancelVoiceInvitation(invitation.invitation_id);
      setInvitations((rows) => rows.map((row) => (
        row.invitation_id === invitation.invitation_id
          ? {
              ...row,
              state: 'revoked',
              consent_status: 'required',
              can_resend: false,
              can_revoke: false,
              can_invite_again: false,
            }
          : row
      )));
      if (activeHouseholdId) {
        try {
          setInvitations(await fetchVoiceInvitations(activeHouseholdId));
        } catch (refreshError) {
          setInvitationError(toApiError(refreshError));
        }
      }
      restoreFocus(`invitation-${invitation.invitation_id}`);
      showResult('Invitation revoked. Its link no longer works.');
    } catch (error) {
      showError(error);
    } finally {
      setInvitationBusyId(null);
    }
  };

  const copyFreshInvitationLink = async () => {
    if (!freshInvitationLink || !isVoiceInvitationCaptureUrl(freshInvitationLink.capture_url)) {
      setCopyFeedback('This link is unavailable. Create a new link and try again.');
      return;
    }
    try {
      await navigator.clipboard.writeText(freshInvitationLink.capture_url);
      setCopyFeedback('Link copied.');
    } catch {
      setCopyFeedback('Could not copy. Select the link and copy it manually.');
    }
  };

  if (loading) {
    return <div className="account-family account-family--center"><StateMessage kind="loading" headingLevel={1} shape="form" title="Loading account and family…" /></div>;
  }

  if (loadError || !household || !state.user) {
    return (
      <div className="account-family account-family--center">
        {loadError ? <StateMessage kind="error" headingLevel={1} error={loadError} title="We couldn’t load your family" retry={{ label: 'Try again', onClick: () => setLoadRetryNonce((nonce) => nonce + 1) }} /> : <EmptyState headingLevel={1} title="Set up your family space" message="Add a household and listener before starting bedtime." action={{ label: 'Go home', to: '/' }} />}
      </div>
    );
  }

  const timezoneOptions = TIMEZONES.includes(timezone) ? TIMEZONES : [timezone, ...TIMEZONES];

  return (
    <div className="account-family">
      <div className="account-family__container">
        <header className="account-family__header">
          <div>
            <h1>Account &amp; family</h1>
          </div>
          {households.length > 1 && (
            <label className="account-family__household-switcher">
              Household
              <select
                value={household.household_id}
                onChange={(event) => setSelectedHouseholdId(event.target.value)}
              >
                {households.map((row, index) => (
                  <option value={row.household_id} key={row.household_id}>Family {index + 1}</option>
                ))}
              </select>
            </label>
          )}
        </header>

        {notice && <div className="account-family__notice" role="status" aria-live="polite">{notice}</div>}
        {actionError && <div className="account-family__action-error"><StateMessage urgent kind="error" error={actionError} title="That change didn’t save" /></div>}

        <div className="account-family__grid">
            <nav className="account-family__section-index" aria-label="Account & family sections">
              <SettingsRow label="Account" icon={<PencilIcon />} expanded={expandedSection === 'account-section'} controls="account-section" onOpen={() => openSection('account-section')} />
              <SettingsRow label="Family" icon={<HomeIcon />} expanded={expandedSection === 'family-section'} controls="family-section" onOpen={() => openSection('family-section')} />
              <SettingsRow label="Bedtime" icon={<MoonIcon />} expanded={expandedSection === 'bedtime-section'} controls="bedtime-section" onOpen={() => openSection('bedtime-section')} />
              <SettingsRow label="Family voices" icon={<MicIcon />} to="/voice" />
              <SettingsRow label="Membership and billing" icon={<BookIcon />} to={`/account/billing?household_id=${encodeURIComponent(household.household_id)}`} />
            </nav>

            <section hidden={expandedSection !== 'account-section'} id="account-section" className="settings-card settings-card--account" aria-labelledby="account-heading">
            <div className="settings-card__heading">
              <div>
                <h2 tabIndex={-1} id="account-heading">Account</h2>
              </div>
            </div>
            <div className="account-profile-row">
              <label className="account-profile-row__portrait" title="Change your picture">
                {state.user.picture ? (
                  <img src={state.user.picture} alt="" />
                ) : (
                  <span>{initials(accountName || state.user.email)}</span>
                )}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  disabled={pictureBusy}
                  aria-label="Change your profile picture"
                  onChange={(event) => void savePicture(event.target.files?.[0])}
                />
              </label>
              <div>
                <strong>{pictureBusy ? 'Saving picture…' : 'Profile picture'}</strong>
                <p>Shown only inside your private family account.</p>
              </div>
            </div>
            <FormField id="account-name" label="Name">
              <input value={accountName} maxLength={200} onChange={(event) => setAccountName(event.target.value)} />
            </FormField>
            <FormField id="account-email" label="Sign-in email" hint="Your sign-in email is managed by your authentication method.">
              <input value={state.user.email} readOnly aria-readonly="true" />

            </FormField>
            <PillButton variant="primary" busy={accountBusy} busyLabel="Saving…" disabled={!accountName.trim()} onClick={() => void saveAccount()}>
              {accountBusy ? 'Saving…' : 'Save account'}
            </PillButton>
            <AnalyticsPreference key={state.user.id} audience="adult" />
          </section>

          <div id="family-section" hidden={expandedSection !== 'family-section'}>
          <section className="settings-card" aria-labelledby="adults-heading">
            <div className="settings-card__heading">
              <div>
                <h2 tabIndex={-1} id="adults-heading">Adults</h2>
              </div>
              <span className="settings-card__count">{household.members.length}</span>
            </div>
            <div className="family-list">
              {household.members.map((member) => (
                <div className="family-row" key={member.membership_id}>
                  <div className="family-avatar">
                    {member.profile_picture_url ? <img src={member.profile_picture_url} alt="" /> : <span>{initials(member.display_name)}</span>}
                  </div>
                  <div className="family-row__identity">
                    <strong>{member.display_name}{member.is_current_user ? ' (you)' : ''}</strong>
                    <span>{member.email}</span>
                  </div>
                  <span className="role-pill">{roleLabel(member.role)}</span>
                  {!member.is_current_user
                    && member.role !== 'owner'
                    && (household.current_user_role === 'owner' || (household.current_user_role === 'admin' && member.role === 'adult'))
                    && <PillButton type="button" variant="destructive" size="compact" busy={memberBusyId === member.membership_id} busyLabel="Removing…" onClick={() => void removeAdult(member.membership_id, member.display_name)}>{memberBusyId === member.membership_id ? 'Removing…' : 'Remove'}</PillButton>}
                </div>
              ))}
            </div>
            <p className="settings-card__note">
              Voice donors use private invitations and do not become household members.
            </p>
          </section>

          <section className="settings-card settings-card--wide" aria-labelledby="listeners-heading">
            <div className="settings-card__heading">
              <div>
                <h2 tabIndex={-1} id="listeners-heading">Who listens at bedtime?</h2>
              </div>
              <PillButton size="compact" onClick={() => setAddingListener((value) => !value)}>
                {addingListener ? <CloseIcon size={18} /> : <PlusIcon size={18} />}
                {addingListener ? 'Cancel' : 'Add listener'}
              </PillButton>
            </div>

            {addingListener && (
              <div className="listener-form listener-form--new">
                <FormField id="new-listener-name" label="Name">
                  <input autoFocus value={newListenerName} maxLength={200} placeholder="First name" onChange={(event) => setNewListenerName(event.target.value)} />
                </FormField>
                <FormField id="new-listener-age" label="Age range (optional)">
                  <select value={newListenerAge} onChange={(event) => setNewListenerAge(event.target.value)}>
                    {AGE_BANDS.map((band) => <option key={band.value} value={band.value}>{band.label}</option>)}
                  </select>
                </FormField>
                <PillButton variant={editingListenerId ? 'secondary' : 'primary'} busy={listenerBusyId === 'new'} busyLabel="Adding…" disabled={!newListenerName.trim()} onClick={() => void addListener()}>
                  {listenerBusyId === 'new' ? 'Adding…' : 'Add to family'}
                </PillButton>
              </div>
            )}

            <div className="listener-grid">
              {household.listeners.map((listener) => (
                <article className={`listener-card ${listener.state === 'archived' ? 'listener-card--archived' : ''}`} key={listener.listener_id}>
                  <div className="listener-card__top">
                    <div className="listener-avatar"><span>{initials(listener.display_name)}</span></div>
                    <div className="listener-card__identity">
                      <strong>{listener.display_name}</strong>
                      <span>{listener.age_band ? `Age range ${listener.age_band}` : 'Age range not set'}</span>
                    </div>
                    <span className={`state-pill state-pill--${listener.state}`}>{listener.state}</span>
                  </div>
                  {editingListenerId === listener.listener_id ? (
                    <div className="listener-form">
                      <FormField id={`listener-name-${listener.listener_id}`} label="Name">
                        <input autoFocus value={listenerName} maxLength={200} onChange={(event) => setListenerName(event.target.value)} />
                      </FormField>
                      <FormField id={`listener-age-${listener.listener_id}`} label="Age range">
                        <select value={listenerAge} onChange={(event) => setListenerAge(event.target.value)}>
                          {AGE_BANDS.map((band) => <option key={band.value} value={band.value}>{band.label}</option>)}
                        </select>
                      </FormField>
                      <div className="listener-form__actions">
                        <PillButton variant="primary" size="compact" busy={listenerBusyId === listener.listener_id} busyLabel="Save" disabled={!listenerName.trim()} onClick={() => void saveListener(listener.listener_id)}>
                          <CheckIcon size={18} /> Save
                        </PillButton>
                        <PillButton size="compact" onClick={() => { restoreFocus(`listener-edit-${listener.listener_id}`); setEditingListenerId(null); }}><CloseIcon size={18} /> Cancel</PillButton>
                      </div>
                    </div>
                  ) : (
                    <div className="listener-card__actions">
                      <PillButton id={`listener-edit-${listener.listener_id}`} size="compact" onClick={() => beginListenerEdit(listener)}><PencilIcon size={18} /> Edit</PillButton>
                      <PillButton id={`listener-state-${listener.listener_id}`} className="listener-card__state-action" busy={listenerBusyId === listener.listener_id} busyLabel={listener.state === 'active' ? 'Archive' : 'Restore'} onClick={() => void setListenerState(listener)}>
                        {listener.state === 'active' ? 'Archive' : 'Restore'}
                      </PillButton>
                    </div>
                  )}
                </article>
              ))}
              {household.listeners.length === 0 && (
                <div className="settings-empty">
                  <p>Add someone who will listen to bedtime stories.</p>
                </div>
              )}
            </div>
          </section>

          <section className="settings-card settings-card--wide" aria-labelledby="voices-heading">
            <div className="settings-card__heading">
              <div>
                <h2 tabIndex={-1} id="voices-heading">Family voices</h2>
              </div>
              <span className="settings-card__count">{state.voices.length}</span>
            </div>
            <p className="settings-card__note">{state.voices.length ? `${state.voices.length} family voice${state.voices.length === 1 ? '' : 's'} saved` : 'No family voices yet.'}</p>
            <div className="settings-card__button-stack">
              <PillButton to="/voice">Open family voices</PillButton>
              <PillButton variant="secondary" to="/voice/invitations">Manage invitations</PillButton>
            </div>
            {invitationError && <div className="account-family__inline-state"><StateMessage kind="error" error={invitationError} title="Invitations could not be refreshed" retry={{ label: 'Try again', onClick: () => setInvitationRetryNonce((nonce) => nonce + 1) }} /></div>}
            {invitations.length > 0 && (
              <div className="invitation-status-list" aria-label="Voice invitation status">
                <h3>Invitation status</h3>
                {invitations.map((invitation) => (
                  <div id={`invitation-${invitation.invitation_id}`} tabIndex={-1} className="invitation-status-row" key={invitation.invitation_id}>
                    <div>
                      <strong>{invitation.donor_name || invitation.recipient_email || 'Invited storyteller'}</strong>
                      <span>For {invitation.listeners.map((listener) => listener.display_name).join(' and ')}{invitation.voice_name ? ` · ${invitation.voice_name}` : ''}</span>
                    </div>
                    <div className="invitation-status-row__states">
                      <span className={`state-pill state-pill--${invitationStatusTone(invitation)}`}>
                        {invitationStatusLabel(invitation)}
                      </span>
                      <small>{invitation.state === 'revoked' ? 'This link no longer works' : invitation.requires_updated_consent_form ? 'Previous consent form' : invitationStateLabel(invitation.state)} · {invitationDeliveryLabel(invitation.delivery_status)}</small>
                      <DeliveryLifecycleStatus deliveries={invitation.lifecycle_delivery} />
                      <small>{formatExpiry(invitation.expires_at)}</small>
                      {(invitation.can_resend || invitation.can_revoke || invitation.can_invite_again || invitation.can_create_updated_invitation) && <div className="invitation-status-row__actions">
                        {invitation.can_resend && <PillButton size="compact" busy={invitationBusyId === invitation.invitation_id} busyLabel={hasEmailDelivery(invitation.delivery_status) ? 'Send again' : 'New link'} onClick={() => void resendInvitation(invitation)} aria-label={`${hasEmailDelivery(invitation.delivery_status) ? 'Send invitation again to' : 'Create a new link for'} ${invitation.recipient_email || 'voice invitation'}`}>{hasEmailDelivery(invitation.delivery_status) ? 'Send again' : 'New link'}</PillButton>}
                        {invitation.can_resend && hasEmailDelivery(invitation.delivery_status) && <PillButton size="compact" busy={invitationBusyId === invitation.invitation_id} busyLabel="Recovery link" onClick={() => void createManualInvitationLink(invitation)} aria-label={`Create a recovery link for ${invitation.recipient_email || 'voice invitation'}`}>Recovery link</PillButton>}
                        {invitation.can_invite_again && <PillButton size="compact" busy={invitationBusyId === invitation.invitation_id} busyLabel="Invite again" onClick={() => void inviteAgain(invitation)} aria-label={`Invite ${invitation.recipient_email || 'voice donor'} again`}>Invite again</PillButton>}
                        {invitation.can_create_updated_invitation && <PillButton size="compact" busy={invitationBusyId === invitation.invitation_id} busyLabel="Create updated invitation" onClick={() => void createUpdatedInvitation(invitation)} aria-label={`Create an updated invitation for ${invitation.recipient_email || 'voice donor'}`}>Create updated invitation</PillButton>}
                        {invitation.can_revoke && <PillButton variant="destructive" size="compact" busy={invitationBusyId === invitation.invitation_id} busyLabel="Revoke" onClick={() => void revokeInvitation(invitation)} aria-label={`Revoke invitation for ${invitation.recipient_email || 'voice donor'}`}>Revoke</PillButton>}
                      </div>}
                    </div>
                    {['consented', 'capturing', 'completed'].includes(invitation.state) && invitation.consent_status !== 'revoked' && <ManagementEmailButton invitationId={invitation.invitation_id} />}
                    {freshInvitationLink?.invitation_id === invitation.invitation_id && <div className="invitation-fresh-link"><p>This fresh link is shown once. The older link no longer works.</p><input aria-label={`Fresh invitation link for ${invitation.recipient_email || 'voice donor'}`} readOnly value={freshInvitationLink.capture_url} onFocus={(event) => event.currentTarget.select()} /><PillButton size="compact" onClick={() => void copyFreshInvitationLink()}>Copy link</PillButton>{copyFeedback && <span role="status">{copyFeedback}</span>}</div>}
                  </div>
                ))}
              </div>
            )}
            <p className="settings-card__note">Voice donors record and consent without creating an account. Household adults keep story approval and playback control.</p>
          </section>
          </div>
          <section hidden={expandedSection !== 'bedtime-section'} id="bedtime-section" className="settings-card" aria-labelledby="bedtime-heading">
            <div className="settings-card__heading">
              <div>
                <h2 tabIndex={-1} id="bedtime-heading">Bedtime preferences</h2>
              </div>
            </div>
            <FormField id="bedtime-timezone" label="Timezone">
              <select value={timezone} onChange={(event) => setTimezone(event.target.value)}>
                {timezoneOptions.map((zone) => <option key={zone} value={zone}>{zone.replace('_', ' ')}</option>)}
              </select>
            </FormField>
            <div className="quiet-hours-grid">
              <FormField id="bedtime-quiet-start" label="Quiet hours begin">
                <input type="time" value={quietStart} onChange={(event) => setQuietStart(event.target.value)} />
              </FormField>
              <FormField id="bedtime-quiet-end" label="Quiet hours end">
                <input type="time" value={quietEnd} onChange={(event) => setQuietEnd(event.target.value)} />
              </FormField>
            </div>
            <label className="settings-toggle">
              <input type="checkbox" checked={renderReadyEmail} onChange={(event) => setRenderReadyEmail(event.target.checked)} />
              <span>
                <strong>Story-ready email</strong>
                <small>Save this preference for future story-ready messages.</small>
              </span>
            </label>
            <p className="settings-card__note">Story-ready emails aren’t available yet.</p>
            <PillButton variant="primary" busy={preferencesBusy} busyLabel="Saving…" onClick={() => void savePreferences()}>
              {preferencesBusy ? 'Saving…' : 'Save preferences'}
            </PillButton>
          </section>


        </div>
        <footer className="account-family__identity"><p>{state.user.full_name || state.user.name || state.user.email}</p><span>{state.user.email}</span></footer>
      </div>
    </div>
  );
};

export default AccountFamily;
